import { Fragment, useEffect, useState } from 'react';
import { Typography, Box, Button, Grid, Dialog, DialogContent, useMediaQuery, IconButton, Divider, Chip } from '@mui/material';
import CloseModalIcon from '../../../icons/RoundCloseIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from '../../../style/Pages/BasicCompanyInfo';
import CalendarIcon from '../../shared/CalendarIcon';
import { InputField } from '../../../components/InputController';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectDateModal from './SelectDateModal';
import moment from 'moment';
import ReviewModal from './ReviewModal';
import { PaymentRequestInfo, getBuildGenericPaymentRequestDto } from '../sagas/paymentrequestslice';
import { AssignedConfigsDto, PaymentRequestDetailDto } from '@inosio/payments-sdk';
import { StyledMaskedInput } from '../../../shared/MaskedInput';
import NumberFormat from 'react-number-format';
import BackIcon from '../../../icons/back1.svg';
import { convertCentsToDollars } from '../../../shared/util/converters';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

interface RequestDetailsModalProps {
  open: boolean;
  propertyGroupUuid: string;
  isInvisible?: boolean;
  onClose: () => void;
  onGoBack?: () => void;
}

export interface PaymentRequestLineItem {
  Description: string;
  Amount: string;
}

interface RequestDetailErrors extends PaymentRequestLineItem {}

const RequestDetailsModal = (props: RequestDetailsModalProps) => {
  //constants
  const classes = useStyles();
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const finalRequestDetails: PaymentRequestDetailDto[] = [];
  const style = {
    height: isOnMobileScreen ? '' : '555px',
  };
  const dispatch = useDispatch();
  //Actual details error
  const initialValues: PaymentRequestLineItem[] = [
    {
      Description: '',
      Amount: '',
    },
  ];

  //Array for tracking their validation
  const initialErrors: RequestDetailErrors[] = [
    {
      Description: '',
      Amount: '',
    },
  ];

  //local states
  const [errorMessage, setErrorMessage] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const [openDateModal, setOpenDateModal] = useState(false);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [formValues, setFormValues] = useState(initialValues);
  const [fieldErrors, setFieldErrors] =useState(initialErrors);

  //selectors
  const { allPropertyGroupsAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const propGroupConfigs = allPropertyGroupsAssignedConfigs?.find((pgc) => pgc.Uuid === props.propertyGroupUuid) || ({} as AssignedConfigsDto);
  const paymentRequestMaxAmount = propGroupConfigs?.MaxAllowedPaymentRequestAmount || 2000000;

  //handlers
  let handleChange = (i: number, e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let newFormValues = [...formValues];
    newFormValues[i][e.target.name as keyof PaymentRequestLineItem] = e.target.value;
    setFormValues(newFormValues);
  };

  let handleBlur = (i: number, e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let newfieldErrors = [...fieldErrors];
    if (formValues[i][e.target.name as keyof PaymentRequestLineItem].length === 0) {
      newfieldErrors[i][e.target.name as keyof PaymentRequestLineItem] = '*Required';
      setFieldErrors(newfieldErrors);
    } else {
      newfieldErrors[i][e.target.name as keyof PaymentRequestLineItem] = '';
      setFieldErrors(newfieldErrors);
    }
  };

  let addFormFields = () => {
    setFormValues([...formValues, { Description: '', Amount: '' }]);
    setFieldErrors([...fieldErrors, { Description: '', Amount: '' }]);
  };

  let removeFormFields = (i: number) => {
    let newFormValues = [...formValues];
    newFormValues.splice(i, 1);
    setFormValues(newFormValues);

    let newErrorValues = [...fieldErrors];
    newErrorValues.splice(i, 1);
    setFieldErrors(newErrorValues);
  };

  const handleSubmit = () => {
    if (isValid) {
      setErrorMessage('');
      setOpenReviewModal(true);
      formValues.forEach((detail) => {
        let amount: number = parseFloat(detail.Amount);
        let amountTwoPlaces: string = amount.toFixed(2);
        finalRequestDetails.push({
          Description: detail.Description,
          Amount: parseFloat(amountTwoPlaces) * 100,
        } as PaymentRequestDetailDto);
      });
      dispatch({
        type: getBuildGenericPaymentRequestDto.type,
        payload: {
          RequestDetails: finalRequestDetails,
          TotalAmount: totalAmount * 100,
          DueDate: date?.toISOString(),
        } as PaymentRequestInfo,
      });
    } else {
      setErrorMessage('Fields cannot be empty');
    }
  };

  const calculateTotal = () => {
    let total = 0;
    formValues.forEach((field) => {
      if (field.Amount.length === 0) {
        total += 0;
      } else {
        let amount: number = parseFloat(field.Amount);
        let amountTwoPlaces: string = amount.toFixed(2);
        total += parseFloat(amountTwoPlaces);
      }
    });
    return total;
  };

  //useEffects

  //Making sure no field is empty
  useEffect(() => {
    formValues.forEach((req) => {
      if (req.Amount.length === 0 || req.Description.length === 0) {
        setIsValid(false);
      } else {
        setIsValid(true);
      }
    });
  }, [formValues]);

  useEffect(() => {
    setTotalAmount(calculateTotal());
  }, [formValues]);

  useEffect(() => {
    if (totalAmount.toString() === 'NaN') {
      setErrorMessage('Amount should contain only numbers');
    } else if (totalAmount * 100 > paymentRequestMaxAmount) {
      setErrorMessage(`Max allowed payment is set to $${new Intl.NumberFormat().format(convertCentsToDollars(paymentRequestMaxAmount))}`);
    } else {
      setErrorMessage('');
    }
  }, [totalAmount]);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        onClose={() => {
          props.onClose();
        }}
        fullScreen={isOnMobileScreen}
        BackdropProps={{
          invisible: props.isInvisible ?? false,
        }}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: 'none',
        }}
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            width: '520px',
            borderRadius: '24px',
            boxShadow: 'none',
          },
        }}
        disableEscapeKeyDown={true}
      >
        <DialogContent>
          <Box
            sx={style}
            component="form"
            noValidate={true}
            onSubmit={(e: any) => {
              e.preventDefault();
            }}
          >
            <Grid container spacing={1} justifyContent="center">
              <Grid item xs={2}></Grid>
              <Grid item xs={8}>
                <Typography
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Request Details
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <img
                  src={CloseModalIcon}
                  alt="close"
                  style={{ cursor: 'pointer', marginLeft: isOnMobileScreen ? '0px' : '40px' }}
                  onClick={() => {
                    props.onClose();
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                {errorMessage ? (
                  <div className={classes.globalError}>
                    <Typography
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#DB3939',
                        textAlign: 'center',
                      }}
                    >
                      {errorMessage}
                    </Typography>
                  </div>
                ) : (
                  ''
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Due Date
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" alignItems={'center'}>
                <CalendarIcon
                  handleClickOpen={() => {
                    setOpenDateModal(true);
                  }}
                />
                <Chip label={moment(date).format('MM/DD/YYYY')} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Request Details
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  maxHeight: errorMessage ? '220px' : '300px',
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 7,
                  },
                  '&::-webkit-scrollbar-track': {
                    boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                    borderRadius: '24px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'darkgrey',
                    borderRadius: '24px',
                  },
                }}
              >
                {formValues.map((element, index) => {
                  return (
                    <Grid container spacing={2} key={index} pl={2} pr={2}>
                      <Grid item xs={1}>
                        {index !== 0 ? (
                          <IconButton onClick={() => removeFormFields(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <></>
                        )}
                      </Grid>
                      <Grid item xs={8}>
                        <InputField
                          name="Description"
                          placeholder="Description"
                          isboxShadow={true}
                          isSlim={true}
                          isError={false}
                          onChange={(e) => handleChange(index, e)}
                          value={element.Description || ''}
                          errors={fieldErrors[index]['Description']}
                          onBlur={(e: any) => handleBlur(index, e)}
                          touched={true}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <StyledMaskedInput
                          onBlur={(e: any) => handleBlur(index, e)}
                          label=""
                          name="Amount"
                          values={element.Amount || '0'}
                          handleChange={(e) => handleChange(index, e)}
                          placeholder="$0.00"
                        />
                      </Grid>
                    </Grid>
                  );
                })}
                <Grid container justifyContent={'center'}>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: '24px',
                      textTransform: 'none',
                      color: '#39A2DB',
                      transition: 'background 0.55s, color 0.55s',
                      borderColor: '#39A2DB',
                      '&:hover': {
                        backgroundColor: '#39A2DB',
                        color: 'white',
                        borderColor: '#39A2DB',
                      },
                    }}
                    onClick={addFormFields}
                  >
                    Add another item
                  </Button>
                </Grid>
              </Grid>
              <Grid item sx={{ position: 'absolute', bottom: 50, width: '100%' }}>
                <Divider />
                <Grid container justifyContent={'space-between'}>
                  <Grid item p={2}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '22px',
                        fontFamily: 'Work Sans',
                      }}
                    >
                      Total
                    </Typography>
                  </Grid>
                  <Grid item p={2}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '22px',
                        fontFamily: 'Work Sans',
                      }}
                    >
                      <NumberFormat
                        thousandsGroupStyle="thousand"
                        value={totalAmount}
                        prefix="$"
                        decimalSeparator="."
                        displayType="text"
                        type="text"
                        thousandSeparator={true}
                        allowNegative={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ position: 'absolute', bottom: 10, right: 17 }}>
              <Button
                variant="contained"
                style={{
                  width: '139px',
                  height: '43px',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  color: 'white',
                  backgroundColor: totalAmount.toString() === 'NaN' ? 'rgba(211, 211, 211, 1)' : '#39A2DB',
                  borderColor: '#39A2DB',
                }}
                onClick={handleSubmit}
                disabled={totalAmount.toString() === 'NaN' || totalAmount * 100 > paymentRequestMaxAmount}
                id="continueDetailsBtn"
              >
                continue
              </Button>
            </Grid>
          </Box>
          <Grid item sx={{ position: 'absolute', top: 20, left: 15 }}>
            <img
              src={BackIcon}
              alt="back"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                props.onGoBack && props.onGoBack();
              }}
            />
          </Grid>
        </DialogContent>
      </Dialog>
      <SelectDateModal
        date={date}
        isInvisible={true}
        onChangeDate={(e) => {
          setDate(e);
          setOpenDateModal(false);
        }}
        open={openDateModal}
        onClose={() => {
          setOpenDateModal(false);
        }}
      />
      <ReviewModal
        open={openReviewModal}
        isInvisible={true}
        onClose={() => {
          setOpenReviewModal(false);
          props.onClose();
        }}
        onGoBack={() => {
          setOpenReviewModal(false);
        }}
      />
    </Fragment>
  );
};

export default RequestDetailsModal;
